import { Component, Input, OnInit } from '@angular/core';

@Component({
  selector: 'app-overview-component',
  templateUrl: './overview-component.component.html',
  styleUrls: ['./overview-component.component.scss'],
  standalone: false
})
export class OverviewComponentComponent implements OnInit {
  @Input() sourcingNeedData: any;
  @Input() allDataAvailable: any = {};
  @Input() outsourcingTypesList: any[] = [];

  activeItems: Array<{ key: string; name: string; emoji: string; status: boolean; data: any; type: string; image?: string }> = [];

  constructor() {}

  ngOnInit(): void {
    this.buildActiveItems();
  }

  ngOnChanges(): void {
    this.buildActiveItems();
  }

  buildActiveItems(): void {
    if (!this.sourcingNeedData || !this.allDataAvailable) return;

    this.activeItems = [];

    const itemsConfig = [
      {
        key: 'is_venue_required',
        dataKey: 'venue_details',
        name: 'Venue',
        emoji: '🏛️',
        type: 'venue'
      },
      {
        key: 'is_experience_required',
        dataKey: 'experience_selection_details',
        name: 'Experience',
        emoji: '🎉',
        type: 'experience'
      },
      {
        key: 'is_furniture_required',
        dataKey: 'furniture_details',
        name: 'Furniture',
        emoji: '🪑',
        type: 'furniture'
      },
      {
        key: 'is_speaker_on_stage_talent',
        dataKey: 'speaker_details',
        name: 'Talent',
        emoji: '🎤',
        type: 'speaker'
      },
      {
        key: 'is_transport_required',
        dataKey: 'transport_details',
        name: 'Transport Requirements',
        emoji: '🚖',
        type: 'transport'
      },
      {
        key: 'is_gift_required',
        dataKey: 'gift_details',
        name: 'Gifts & Merchandise',
        emoji: '🎁',
        type: 'gift'
      },
      {
        key: 'is_catering_required',
        dataKey: 'catering_details',
        name: 'Catering',
        emoji: '🍽️',
        type: 'catering'
      },
      {
        key: 'is_photographer_videographer_required',
        dataKey: 'sourcing_need_photo_video_details',
        name: 'Videographer & Photographer',
        emoji: '🎥',
        type: 'photographer'
      },
      {
        key: 'is_branding_collateral_required',
        dataKey: 'branding_collateral_details',
        name: 'Branding & Collateral',
        emoji: '🏷️',
        type: 'branding'
      },
      {
        key: 'is_av_required',
        dataKey: 'av_requirements_details',
        name: 'AV Requirements',
        emoji: '🎧',
        type: 'av'
      },
      {
        key: 'is_production_fabrication_required',
        dataKey: 'production_and_fabrication_details',
        name: 'Production & Fabrication',
        emoji: '🏗️',
        type: 'productionFabrication'
      }
    ];

    itemsConfig.forEach(item => {
      const isRequired = this.sourcingNeedData[item.key] ?? false;
      let data = this.sourcingNeedData[item.dataKey] ?? null;

       // Specific normalization for speaker/furniture/experience as seen in parent component
       if (item.type === 'speaker' && data) {
         data = this.normalizeViewData(data, 'speaker_details');
       } else if (item.type === 'furniture' && data) {
         data = this.normalizeViewData(data, 'furniture_details');
       } else if (item.type === 'experience' && data) {
         // Experience now uses separate model - take first item from array
         data = Array.isArray(data) && data.length > 0 ? data[0] : null;
       } else if(item.type === 'gift' && (this.sourcingNeedData.gift_details || this.sourcingNeedData.gift_rate)){
            data = {
                gift_details: this.sourcingNeedData.gift_details,
                gift_rate: this.sourcingNeedData.gift_rate
            };
        } else if (item.type === 'branding' && data) {
          data = this.withStageSizeDisplay(data);
       }

      // Find matching image
      const matchingType = this.findOutsourcingTypeByName(item.name);
      const image = matchingType ? matchingType.image : undefined;

      if (isRequired && data) {
        this.activeItems.push({
          key: item.key,
          name: item.name,
          emoji: item.emoji,
          status: true,
          data: data,
          type: item.type,
          image: image
        });
      }
    });
  }

  // Helper from parent component to normalize specific data structures
  normalizeViewData(data: any, key: string): any {
     if (Array.isArray(data)) {
         return { [key]: data };
     }
     if (typeof data === 'string') {
          return { [key]: data };
     }
     return data;
  }

  getStatusLabel(status: boolean): string {
    return status ? 'Active' : 'Inactive';
  }

  getStatusColor(status: boolean): string {
    return status ? '#52c41a' : '#d9d9d9';
  }

  getViewConfig(type: string): any[] {
    switch (type) {
      case 'av':
        return [
          { key: 'microphone_details', label: 'Microphones', type: 'array' },
          { key: 'speakers_audio_output_details', label: 'Speakers & Audio', type: 'array' },
          { key: 'audio_equipment_details', label: 'Audio Equipment', type: 'array' },
          { key: 'support_infra_details', label: 'Support Infra', type: 'array' },
          { key: 'av_staff_details', label: 'AV Staff', type: 'array' },
          { key: 'display_units_details', label: 'Display Systems', type: 'array' },
          { key: 'cameras_recording_details', label: 'Cameras & Recording', type: 'array' },
          { key: 'video_equipment_details', label: 'Video Equipment', type: 'array' },
          { key: 'general_stage_lighting_details', label: 'Stage Lighting', type: 'array' },
          { key: 'special_lighting_details', label: 'Ambient & Decorative Lighting', type: 'array' },
          { key: 'lighting_control_details', label: 'Lighting Control', type: 'array' },
          { key: 'rigging_support_details', label: 'Rigging Support', type: 'array' }
        ];
      case 'venue':
        return [
          // Legacy single venue/room type/seating style fields (kept for records created before
          // the multi-venue-room payload change). New records are shown via venue_rooms_details below.
          { key: 'venue_name', label: 'Venue', type: 'text', legacy: true },
          { key: 'room_type_name', label: 'Room Type', type: 'text', legacy: true },
          { key: 'seating_style_name', label: 'Seating Style', type: 'text', legacy: true },
          {
            key: 'venue_rooms_details', label: 'Venue, Room Type & Seating Style', type: 'group-array', fullWidth: true,
            groupFields: [
              { key: 'venue_name', label: 'Venue' },
              { key: 'room_type_name', label: 'Room Type' },
              { key: 'seating_style_name', label: 'Seating Style' }
            ]
          },
          { key: 'is_breakout_rooms_required', label: 'Breakout Rooms', type: 'boolean' },
          { key: 'number_of_breakout_rooms', label: 'No. of Breakout Rooms', type: 'text' }
        ];
      case 'catering':
        return [
          { key: 'catering_type_details', label: 'Catering Type', type: 'array' },
          { key: 'meal_type_details', label: 'Meal Type', type: 'array' },
          { key: 'meal_timing_details', label: 'Meal Timings', type: 'array' },
          { key: 'non_alcoholic_beverage_details', label: 'Non-Alcoholic Beverage', type: 'array' },
          { key: 'alcoholic_beverage_details', label: 'Alcoholic Beverage', type: 'array' },
          { key: 'dietary_preferences_details', label: 'Dietary Preferences', type: 'array' },
          { key: 'liquor_license_required', label: 'Liquor License', type: 'boolean' }
        ];
      case 'photographer':
        return [
          { key: 'video_description', label: 'No. of Videographers', type: 'text' },
          { key: 'photo_description', label: 'No. of Photographers', type: 'text' }
        ];
      case 'branding':
        return [
          { key: 'is_stage_required', label: 'Stage Required', type: 'boolean' },
          { key: 'stage_size_display', label: 'Stage Size', type: 'text', conditional: true },
          { key: 'stage_accessories_details', label: 'Stage & Stage Accessories', type: 'array' },
          { key: 'guest_management_details', label: 'Guest Management', type: 'array' },
          { key: 'signage_branding_details', label: 'Signage & Branding', type: 'array' },
          { key: 'demo_engagement_details', label: 'Demo & Engagement', type: 'array' },
          { key: 'media_advertising_details', label: 'Media Advertising', type: 'array' },
          { key: 'setup_branding_details', label: 'Setup & Branding', type: 'array' }
        ];
      case 'gift':
        return [
          { key: 'gift_details', label: 'Gifts & Merchandise Details', type: 'text' },
          { key: 'gift_rate', label: 'Budget (USD)', type: 'text' }
        ];
      case 'transport':
        return [
          { key: 'airport_transfers_details', label: 'Airport Transfers', type: 'array' },
          { key: 'local_venue_transfers_details', label: 'Local/Venue Transfers', type: 'array' },
          { key: 'vehicles_type_details', label: 'Vehicle Types', type: 'array' }
        ];
      case 'speaker':
        return [
          { key: 'speaker_details', label: 'Speaker Details', type: 'array' }
        ];
      case 'furniture':
          return [
          { key: 'furniture_details', label: 'Furniture Details', type: 'text' }
        ];
      case 'experience':
        return [
          { key: 'experience_type_details.name', label: 'Experience Type', type: 'text' },
          { key: 'experience_category_details.name', label: 'Experience Category', type: 'text' },
          { key: 'experience_testing_type_details.name', label: 'Experience Testing Type', type: 'text', conditional: true }
        ];
      case 'productionFabrication':
        return [
          { key: 'event_structures_details', label: 'Event Structures', type: 'array' },
          { key: 'infrastructure_details', label: 'Infrastructure', type: 'array' },
          { key: 'live_streaming_hybrid_details', label: 'Live Streaming & Hybrid', type: 'array' },
          { key: 'intercom_communication_details', label: 'Intercom & Communication', type: 'array' }
        ];
      default:
        return [];
    }
  }

  private findOutsourcingTypeByName(name: string): any {
    const normalizedTarget = this.normalizeTypeName(name);
    return this.outsourcingTypesList.find((type) => this.normalizeTypeName(type?.name) === normalizedTarget);
  }

  private normalizeTypeName(name: string): string {
    return (name || '').toLowerCase().replace(/&/g, 'and').replace(/\s+/g, ' ').trim();
  }

  private withStageSizeDisplay(data: any): any {
    if (!data || typeof data !== 'object') {
      return data;
    }

    const stageRequired = !!data.is_stage_required;
    const width = Number(data.stage_dimension_x);
    const height = Number(data.stage_dimension_y);

    let stageSizeDisplay: string | null = null;
    if (stageRequired && Number.isFinite(width) && Number.isFinite(height)) {
      if (width === 8 && height === 8) {
        stageSizeDisplay = 'Small (8x8)';
      } else if (width === 12 && height === 8) {
        stageSizeDisplay = 'Medim (12x8)';
      } else if (width === 16 && height === 8) {
        stageSizeDisplay = 'Large (16x8)';
      } else {
        stageSizeDisplay = `Custom (${width}x${height})`;
      }
    }

    return {
      ...data,
      stage_size_display: stageSizeDisplay,
    };
  }
}
